import { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { useCitySearch } from '@/hooks/useGeography';
import { cn } from '@/lib/utils';
import { MapPin, Loader2 } from 'lucide-react';
import type { CitySearchResult } from '@/services/geography.service';

interface CityAutocompleteProps {
  /** Currently selected city */
  value?: CitySearchResult | null;
  /** Called when a city is selected */
  onSelect: (city: CitySearchResult | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional className for the input */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Display format for the selected value */
  displayFormat?: (city: CitySearchResult) => string;
}

const defaultDisplayFormat = (city: CitySearchResult): string =>
  `${city.name} (${city.postalCode})`;

/**
 * City autocomplete component with search by name or postal code.
 * Integrates with the geography API for French cities.
 */
export function CityAutocomplete({
  value,
  onSelect,
  placeholder = 'Rechercher une ville ou code postal...',
  className,
  disabled = false,
  displayFormat = defaultDisplayFormat,
}: CityAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: results = [], isLoading } = useCitySearch(debouncedQuery);

  // Sync display value when external value changes
  useEffect(() => {
    if (value) {
      setInputValue(displayFormat(value));
    }
  }, [value, displayFormat]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setOpen(true);

    // Clear selection if user modifies the text
    if (value) {
      onSelect(null);
    }

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(val.trim());
    }, 300);
  }, [value, onSelect]);

  const handleSelect = useCallback((city: CitySearchResult) => {
    onSelect(city);
    setInputValue(displayFormat(city));
    setOpen(false);
  }, [onSelect, displayFormat]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setDebouncedQuery('');
    onSelect(null);
    inputRef.current?.focus();
  }, [onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  return (
    <Popover open={open && (results.length > 0 || isLoading)} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (inputValue.length >= 2 && !value) {
                setOpen(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('pl-8 pr-8', className)}
          />
          {isLoading && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {inputValue && !isLoading && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
              tabIndex={-1}
            >
              ✕
            </button>
          )}
        </div>
      </PopoverAnchor>

      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ul className="max-h-60 overflow-y-auto py-1">
          {results.map((city) => (
            <li key={`${city.id}-${city.postalCode}`}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                  value?.id === city.id && value?.postalCode === city.postalCode && 'bg-accent'
                )}
                onClick={() => handleSelect(city)}
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 text-left">
                  <span className="font-medium">{city.name}</span>
                  <span className="text-muted-foreground ml-1">({city.postalCode})</span>
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {city.departement}
                </span>
              </button>
            </li>
          ))}
          {isLoading && (
            <li className="px-3 py-2 text-sm text-muted-foreground text-center">
              Recherche en cours...
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
